"use client";

import { Button } from "../../forms/Button/Button";
import { DSCopyButton } from "../../forms/Button/CopyButton";
import { Alert } from "../Alert/Alert";

type UnhandledRunTimeErrorI18n = {
	title: string;
	message: React.ReactNode;
	reloadButton: string;
	copied: string;
	copyToClipboard: string;
	copyFailed: string;
	copyBlocked: string;
};

type UnhandledRunTimeErrorProps = {
	eventId: string | null;
	error: unknown;
	onReload: () => void;
	i18n: UnhandledRunTimeErrorI18n;
};

const serializeError = (error: unknown): string => {
	if (error instanceof Error) {
		return JSON.stringify({ name: error.name, message: error.message });
	}
	if (typeof error === "string") {
		return JSON.stringify({ message: error });
	}
	return JSON.stringify({ message: "Unknown error" });
};

const serializeErrorVerbose = (error: unknown): string => {
	if (error instanceof Error) {
		return [`${error.name}: ${error.message}`, error.stack ?? ""].filter(Boolean).join("\n\n");
	}
	if (typeof error === "string") {
		return error;
	}
	return "Unknown error";
};

export const UnhandledRunTimeError = ({ eventId, error, onReload, i18n }: UnhandledRunTimeErrorProps) => {
	const supportLabel = eventId ? `RUM Ref: ${eventId}` : serializeError(error);

	return (
		<div className="flex w-full flex-col items-center justify-center py-5 font-sans">
			<Alert type="error" title={i18n.title} message={i18n.message} />
			<div className="mt-2 flex items-center gap-2">
				<p className="text-critical underline">{eventId ? `RUM Ref: ${eventId}` : "For support:"}</p>
				<DSCopyButton
					text={supportLabel}
					size="xs"
					i18n={{
						copied: i18n.copied,
						copyToClipboard: i18n.copyToClipboard,
						copyFailed: i18n.copyFailed,
						copyBlocked: i18n.copyBlocked,
					}}
				/>
			</div>
			{!eventId && (
				<pre className="mt-1 max-w-md overflow-auto rounded bg-neutral-faded p-2 text-neutral-faded text-xs">
					{serializeError(error)}
				</pre>
			)}
			<Button type="button" size="sm" intent="primary" className="mt-4" onClick={onReload}>
				{i18n.reloadButton}
			</Button>
		</div>
	);
};

export { serializeErrorVerbose };
